import { Property } from './Property.ts';
import { normalize, PropsWithType, xssSanitize } from '@utils';

export class PropertyFilters {
  aims: string[] = [];
  areas: string[] = [];
  subAreas: string[] = [];
  categories: string[] = [];
  search: string = '';
  subCategories: string[] = [];
  minPrice: number = 0;
  maxPrice: number = +Infinity;
  minSqrMeters: number = 0;
  maxSqrMeters: number = +Infinity;
  minPricePerSqrMeter: number = 0;
  maxPricePerSqrMeter: number = +Infinity;
  minBuildingYear: number = 0;
  maxBuildingYear: number = +Infinity;
  minRenovationYear: number = 0;
  maxRenovationYear: number = +Infinity;
  minRooms: number = 0;
  maxRooms: number = +Infinity;
  minMasterBedrooms: number = 0;
  maxMasterBedrooms: number = +Infinity;
  minBathrooms: number = 0;
  maxBathrooms: number = +Infinity;
  minWc: number = 0;
  maxWc: number = +Infinity;
  minParkings: number = 0;
  maxParkings: number = +Infinity;

  constructor(init?: Partial<PropertyFilters>) {
    Object.assign(this, init);
  }

  initSliderFilter(
    minFilter: keyof PropsWithType<PropertyFilters, number>,
    maxFilter: keyof PropsWithType<PropertyFilters, number>,
    propertyField: keyof PropsWithType<Property, number>,
    allProperties: Property[],
  ) {
    const min =
      allProperties.length > 0
        ? Math.min(...allProperties.map((prop) => prop[propertyField]))
        : 0;
    const max =
      allProperties.length > 0
        ? Math.max(...allProperties.map((prop) => prop[propertyField]))
        : +Infinity;
    this[minFilter] = min;
    this[maxFilter] = max;
    return this;
  }

  initSliderFilters(allProperties: Property[]) {
    this.initSliderFilter('minPrice', 'maxPrice', 'price', allProperties);
    this.initSliderFilter(
      'minSqrMeters',
      'maxSqrMeters',
      'sqrMeters',
      allProperties,
    );
    this.initSliderFilter(
      'minPricePerSqrMeter',
      'maxPricePerSqrMeter',
      'pricePerSqrm',
      allProperties,
    );
    this.initSliderFilter(
      'minBuildingYear',
      'maxBuildingYear',
      'buildingYear',
      allProperties,
    );
    this.initSliderFilter(
      'minRenovationYear',
      'maxRenovationYear',
      'renovationYear',
      allProperties,
    );
    this.initSliderFilter('minRooms', 'maxRooms', 'rooms', allProperties);
    this.initSliderFilter(
      'minMasterBedrooms',
      'maxMasterBedrooms',
      'masterBedrooms',
      allProperties,
    );
    this.initSliderFilter(
      'minBathrooms',
      'maxBathrooms',
      'bathrooms',
      allProperties,
    );
    this.initSliderFilter('minWc', 'maxWc', 'wc', allProperties);
    this.initSliderFilter(
      'minParkings',
      'maxParkings',
      'totalParkings',
      allProperties,
    );
    return this;
  }

  testCheckboxFilter(
    filter: keyof PropsWithType<PropertyFilters, string[]>,
    propertyField: keyof PropsWithType<Property, string>,
    prop: Property,
  ) {
    return (
      this[filter].length === 0 || this[filter].includes(prop[propertyField])
    );
  }

  testAimsFilter(prop: Property) {
    return this.testCheckboxFilter('aims', 'aim', prop);
  }
  testAreasFilter(prop: Property) {
    return this.testCheckboxFilter('areas', 'area', prop);
  }
  testSubAreasFilter(prop: Property) {
    return this.testCheckboxFilter('subAreas', 'subArea', prop);
  }
  testCategoriesFilter(prop: Property) {
    return this.testCheckboxFilter('categories', 'category', prop);
  }
  testSubCategoriesFilter(prop: Property) {
    return this.testCheckboxFilter('subCategories', 'subCategory', prop);
  }

  testSliderFilter(
    minFilter: keyof PropsWithType<PropertyFilters, number>,
    maxFilter: keyof PropsWithType<PropertyFilters, number>,
    propertyField: keyof PropsWithType<Property, number>,
    prop: Property,
  ) {
    return (
      this[minFilter] <= prop[propertyField] &&
      prop[propertyField] <= this[maxFilter]
    );
  }

  // Returns a score for the property, 0 if the property does not match the filter
  // 1 or higher if the property matches the filter
  // The higher the score, the more relevant the property is to the search text
  testSearchFilter(prop: Property) {
    const searchText = normalize(this.search);
    if (searchText.length < 2) return 1;

    const evaluateTerm = (searchTerm: string) => {
      const candidates: number[] = [];

      const pushScore = (
        value: string | number | undefined | null,
        base: number,
      ) => {
        if (value === undefined || value === null) return;
        const text = typeof value === 'number' ? value.toString() : value;
        const norm = normalize(text);
        if (!norm) return;
        if (norm.includes(searchTerm)) {
          let s = base;
          // Boost whole-word matches
          const words = new Set(norm.split(' ').filter(Boolean));
          if (words.has(searchTerm)) s += 2;
          candidates.push(s);
        }
      };

      // Strong identifiers/numerics
      const idStr = prop.id?.toString();
      if (
        idStr &&
        (searchTerm === idStr ||
          searchTerm.includes(idStr) ||
          idStr.includes(searchTerm))
      ) {
        candidates.push(10); // highest weight for direct id search
      }
      if (prop.customCode) {
        const cc = normalize(prop.customCode);
        if (cc && (cc.includes(searchTerm) || searchTerm.includes(cc))) {
          candidates.push(9);
        }
      }
      if (prop.postalCode) {
        const pc = normalize(prop.postalCode);
        if (pc && (pc.includes(searchTerm) || searchTerm.includes(pc))) {
          candidates.push(9);
        }
      }

      // Content fields
      pushScore(prop.title, 6);
      pushScore(prop.description, 4);
      pushScore(prop.advertisement, 3);
      pushScore(prop.areaDescription, 3);
      pushScore(prop.buildingName, 5);

      // Taxonomy/location
      pushScore(prop.subCategory, 6);
      pushScore(prop.category, 5);
      pushScore(prop.aim, 5);
      pushScore(prop.subArea, 8);
      pushScore(prop.area, 7);

      // Characteristics
      for (const characteristic of prop.characteristics) {
        pushScore(characteristic.title, 2);

        for (const characteristicValue of characteristic.types) {
          pushScore(characteristicValue, 1);
        }
      }

      return candidates.length ? Math.max(...candidates) : 0;
    };

    let totalScore = 0;
    const searchTerms = searchText.split(' ').filter(Boolean);

    // AND semantics: every term must have a match somewhere; otherwise the property is irrelevant
    for (const term of searchTerms) {
      const termScore = evaluateTerm(term);
      if (termScore === 0) return 0;
      totalScore += termScore;
    }

    return totalScore;
  }

  testPriceFilter(prop: Property) {
    return this.testSliderFilter('minPrice', 'maxPrice', 'price', prop);
  }
  testSqrMetersFilter(prop: Property) {
    return this.testSliderFilter(
      'minSqrMeters',
      'maxSqrMeters',
      'sqrMeters',
      prop,
    );
  }
  testPricePerSqrMeterFilter(prop: Property) {
    return this.testSliderFilter(
      'minPricePerSqrMeter',
      'maxPricePerSqrMeter',
      'pricePerSqrm',
      prop,
    );
  }
  testBuildingYearFilter(prop: Property) {
    return this.testSliderFilter(
      'minBuildingYear',
      'maxBuildingYear',
      'buildingYear',
      prop,
    );
  }
  testRenovationYearFilter(prop: Property) {
    return this.testSliderFilter(
      'minRenovationYear',
      'maxRenovationYear',
      'renovationYear',
      prop,
    );
  }
  testRoomsFilter(prop: Property) {
    return this.testSliderFilter('minRooms', 'maxRooms', 'rooms', prop);
  }
  testMasterBedroomsFilter(prop: Property) {
    return this.testSliderFilter(
      'minMasterBedrooms',
      'maxMasterBedrooms',
      'masterBedrooms',
      prop,
    );
  }
  testBathroomsFilter(prop: Property) {
    return this.testSliderFilter(
      'minBathrooms',
      'maxBathrooms',
      'bathrooms',
      prop,
    );
  }
  testWcFilter(prop: Property) {
    return this.testSliderFilter('minWc', 'maxWc', 'wc', prop);
  }
  testParkingsFilter(prop: Property) {
    return this.testSliderFilter(
      'minParkings',
      'maxParkings',
      'totalParkings',
      prop,
    );
  }

  static fromUrlParams(params: URLSearchParams) {
    const filters = new PropertyFilters();

    for (const [key, value] of params.entries()) {
      if (!value) continue;
      switch (key) {
        case 'aim':
          filters.aims.push(value);
          break;
        case 'area':
          filters.areas.push(value);
          break;
        case 'subArea':
          filters.subAreas.push(value);
          break;
        case 'category':
          filters.categories.push(value);
          break;
        case 'subCategory':
          filters.subCategories.push(value);
          break;
        case 'search':
          filters.search = xssSanitize(value);
          break;
      }
    }

    return filters;
  }
}
